import { createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'

import restClient, { ApiRejectResponse, validateApiError } from 'lib/api/restClient'
import apiRoutes from 'lib/api/apiRoutes'
import { calculateExpiresAt, storeAccessToken, clearAccessToken, clearCodeVerifier } from 'lib/oauth2/bccOAuth2Utils'
import logger from 'lib/logger'

export interface TokenExchangeError {
  message: string
  exceptionClass?: string
  contextId?: string
}

export interface ExchangeCodeForTokensRequest {
  code: string
  codeVerifier: string
}

export interface ExchangeCodeForTokensResponse {
  accessToken: string
  expiresIn: number
  sessionSecret?: string
}

export interface RefreshAccessTokenResponse {
  accessToken: string
  expiresIn: number
}

export interface ValidateSessionRequest {
  sessionSecret: string
}

export interface ValidateSessionResponse {
  valid: boolean
  expectedExpirationTime: number
  userId: number
}

export const exchangeCodeForTokens = createAsyncThunk<
  ExchangeCodeForTokensResponse & { expiresAt: number },
  ExchangeCodeForTokensRequest,
  ApiRejectResponse<TokenExchangeError>
>('bccAuth/exchangeCodeForTokens', async (payload, { rejectWithValue }) => {
  try {
    const resp = await restClient(apiRoutes.BCC_OAUTH2_TOKEN_EXCHANGE, {
      data: {
        code: payload.code,
        code_verifier: payload.codeVerifier
      }
    })

    // Clear the code verifier after use
    clearCodeVerifier()

    const { access_token: accessToken, expires_in: expiresIn, session_secret: sessionSecret } = resp.data
    const expiresAt = calculateExpiresAt(expiresIn)

    // Store in sessionStorage for persistence across page refreshes
    storeAccessToken(accessToken, expiresAt)

    return {
      accessToken,
      expiresIn,
      expiresAt,
      sessionSecret
    }
  } catch (e) {
    // Clear the code verifier on failure to prevent reuse
    clearCodeVerifier()

    // API gateway returns structured errors with exception_class and message
    const errorData = axios.isAxiosError(e) ? e.response?.data : null
    const error: TokenExchangeError = {
      message: errorData?.message || validateApiError(e),
      exceptionClass: errorData?.exception_class,
      contextId: errorData?.context_id
    }

    logger.error(
      `[BCC OAuth2] Token exchange failed: ${error.exceptionClass || 'unknown'} - ${error.message} (contextId: ${
        error.contextId
      })`
    )
    return rejectWithValue(error)
  }
})

export interface RefreshAccessTokenRequest {
  sessionSecret: string
}

export const refreshAccessToken = createAsyncThunk<
  RefreshAccessTokenResponse & { expiresAt: number },
  RefreshAccessTokenRequest,
  ApiRejectResponse
>('bccAuth/refreshAccessToken', async (payload, { rejectWithValue }) => {
  try {
    const resp = await restClient(apiRoutes.BCC_OAUTH2_REFRESH, {
      data: {
        session_secret: payload.sessionSecret
      }
    })

    const { access_token: accessToken, expires_in: expiresIn } = resp.data
    const expiresAt = calculateExpiresAt(expiresIn)

    // Store in sessionStorage for persistence across page refreshes
    storeAccessToken(accessToken, expiresAt)

    return {
      accessToken,
      expiresIn,
      expiresAt
    }
  } catch (e) {
    // Clear stored token on refresh failure
    clearAccessToken()
    return rejectWithValue(validateApiError(e))
  }
})

export interface ValidateSessionError {
  message: string
  statusCode?: number
  isTransient: boolean
}

export const validateSession = createAsyncThunk<
  ValidateSessionResponse,
  ValidateSessionRequest,
  ApiRejectResponse<ValidateSessionError>
>('bccAuth/validateSession', async (payload, { rejectWithValue }) => {
  try {
    const resp = await restClient(apiRoutes.BCC_OAUTH2_SESSION_VALIDATE, {
      data: {
        session_secret: payload.sessionSecret
      }
    })

    const { valid, expected_expiration_time: expectedExpirationTime, user_id: userId } = resp.data

    return {
      valid,
      expectedExpirationTime,
      userId
    }
  } catch (e) {
    // restClient's axios interceptor rejects with error.response, but handle both cases
    const statusCode = axios.isAxiosError(e) ? e.response?.status : e?.status
    // 5xx errors are transient (server issues) - don't invalidate session
    // 4xx errors are definitive (invalid session) - should trigger logout
    // Network errors (undefined statusCode) are transient - preserve session
    const isTransient = statusCode === undefined || statusCode >= 500

    logger.error(`[BCC OAuth2] Session validation failed: ${statusCode || 'unknown'} (isTransient: ${isTransient})`)

    return rejectWithValue({
      message: isTransient ? 'Session validation temporarily unavailable' : 'BCC session validation failed',
      statusCode,
      isTransient
    })
  }
})
